import React, { FC, useMemo } from 'react'
import { Divider, Collapse, Card, Typography } from 'antd'

const { Text } = Typography

// info-row styles — match page.tsx tokens
const rowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  alignItems: 'baseline',
  padding: '5px 0',
  borderBottom: '1px solid var(--crm-border)',
}

const labelStyle: React.CSSProperties = {
  fontSize: 14,
  color: 'var(--crm-text-secondary)',
  flexShrink: 0,
}

const valueStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  fontFamily: 'monospace',
  textAlign: 'left',
  wordBreak: 'break-all',
}

function formatFieldName(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim()
}

type FieldItem = {
  FieldName: string
  Value: React.ReactNode
  orderSequence: number
}

export type FieldFormatter = (
  value: React.ReactNode,
  fieldName: string,
  context?: any,
) => React.ReactNode

type FormDataFieldsProps = {
  fields: FieldItem[]
  displayMode?: 'divider' | 'collapse' | 'card'
  fieldFormatters?: Record<string, FieldFormatter>
  context?: any
  hideExtra?: boolean
}

const SECONDARY_ORDER_THRESHOLD = 1000

function mergeNameFields(fields: FieldItem[]): FieldItem[] {
  const firstNameField = fields.find((f) => f.FieldName === 'firstName')
  const lastNameField = fields.find((f) => f.FieldName === 'lastName')

  if (!firstNameField && !lastNameField) return fields

  const holderValue = [firstNameField?.Value, lastNameField?.Value]
    .filter(Boolean)
    .join(' ')

  const holderSeq = Math.min(
    firstNameField?.orderSequence ?? Infinity,
    lastNameField?.orderSequence ?? Infinity,
  )

  const holderField: FieldItem = {
    FieldName: 'Holder Name',
    Value: holderValue,
    orderSequence: holderSeq,
  }

  return [
    ...fields.filter(
      (f) => f.FieldName !== 'firstName' && f.FieldName !== 'lastName',
    ),
    holderField,
  ]
}

function groupByOrderSequence(fields: FieldItem[]): FieldItem[] {
  const grouped: Record<number, FieldItem[]> = {}

  for (const field of fields) {
    grouped[field.orderSequence] ??= []
    grouped[field.orderSequence].push(field)
  }

  return Object.entries(grouped)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([seqStr, items]) => ({
      FieldName: items.map((i) => i.FieldName).join(' '),
      Value: items.map((i) => i.Value).join(' '),
      orderSequence: Number(seqStr),
    }))
}

export const FormDataFields: FC<FormDataFieldsProps> = ({
  fields,
  displayMode = 'divider',
  fieldFormatters = {},
  context,
  hideExtra = false,
}) => {
  const { primaryOrdered, secondaryOrdered, unordered } = useMemo(() => {
    const unordered = fields.filter((f) => f.orderSequence === 0)
    const orderedFields = fields.filter((f) => f.orderSequence > 0)

    const withMergedNames = mergeNameFields(orderedFields)
    const combined = groupByOrderSequence(withMergedNames)

    return {
      primaryOrdered: combined.filter(
        (f) => f.orderSequence < SECONDARY_ORDER_THRESHOLD,
      ),
      secondaryOrdered: combined.filter(
        (f) => f.orderSequence >= SECONDARY_ORDER_THRESHOLD,
      ),
      unordered,
    }
  }, [fields])

  if (!fields.length) return null

  const renderField = (item: FieldItem) => {
    const formatter = fieldFormatters[item.FieldName]
    const value = formatter
      ? formatter(item.Value, item.FieldName, context)
      : item.Value

    return (
      <div key={item.FieldName} style={rowStyle}>
        <span style={labelStyle}>{formatFieldName(item.FieldName)}</span>
        <span style={valueStyle}>{value}</span>
      </div>
    )
  }

  const hasOrderedFields =
    primaryOrdered.length > 0 || secondaryOrdered.length > 0

  const renderUnorderedSection = () => {
    if (unordered.length === 0) return null

    if (!hasOrderedFields) return <>{unordered.map(renderField)}</>

    switch (displayMode) {
      case 'divider':
        return (
          <>
            <Divider />
            <Text type="secondary">Дополнительные поля</Text>
            {unordered.map(renderField)}
          </>
        )
      case 'collapse':
        return (
          <Collapse ghost className="mt-3">
            <Collapse.Panel
              header={`Дополнительные поля (${unordered.length})`}
              key="extra"
            >
              {unordered.map(renderField)}
            </Collapse.Panel>
          </Collapse>
        )
      case 'card':
        return (
          <Card
            size="small"
            type="inner"
            title="Дополнительные поля"
            style={{ marginTop: 16 }}
          >
            {unordered.map(renderField)}
          </Card>
        )
    }
  }

  return (
    <>
      {primaryOrdered.map(renderField)}
      {secondaryOrdered.length > 0 && (
        <div style={{ marginTop: 12 }}>{secondaryOrdered.map(renderField)}</div>
      )}
      {!hideExtra && renderUnorderedSection()}
    </>
  )
}
